/**
 * Copyright © 2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { useCartContext } from "@farmlands/commerce";
import { useCallback } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import ToastIcon from "../../../components/ToastIcon";
import { useAppDispatch } from "../../../store/rootStore";
import { selectCurrentFarmerAccount } from "../../azure-b2c/storage";
import { Product } from "../../products/storage";
import CartSlice from "../storage/cart.slice";
import { CartItem } from "../storage/cart.types";
import { mapToCommerceAddToCartItem } from "../utils/commerce-cart-mapping";

export const useCartItemActions = (item: Product | CartItem) => {
  const dispatch = useAppDispatch();
  const currentAccount = useSelector(selectCurrentFarmerAccount);
  const {
    addToCart: addToCommerceCart,
    setQuantity: setCommerceQuantity,
    removeItem: removeCommerceItem,
  } = useCartContext();

  const notifyCartUpdated = useCallback((): void => {
    toast.dismiss("cart-updated");

    setTimeout(() => {
      toast("Cart updated", {
        toastId: "cart-updated",
        type: "warning",
        icon: <ToastIcon>check_circle</ToastIcon>,
      });
    }, 1000);
  }, []);

  const handleAddToCart = useCallback((): void => {
    if (currentAccount) {
      const product = {
        ...item,
        quantity: 1,
        productId: parseInt(item.productId.toString(), 10),
      };

      dispatch(
        CartSlice.actions.addToCart({
          accountNumber: currentAccount.accountNumber,
          product,
        })
      );
      addToCommerceCart(mapToCommerceAddToCartItem(product, 1));

      notifyCartUpdated();
    }
  }, [addToCommerceCart, currentAccount, dispatch, item, notifyCartUpdated]);

  const setQuantity = useCallback(
    (value: number): void => {
      if (currentAccount) {
        dispatch(
          CartSlice.actions.setQuantity({
            accountNumber: currentAccount.accountNumber,
            product: item as CartItem,
            amount: value,
          })
        );
        setCommerceQuantity(parseInt(item.productId.toString(), 10), value);

        notifyCartUpdated();
      }
    },
    [currentAccount, dispatch, item, notifyCartUpdated, setCommerceQuantity]
  );

  const removeItem = useCallback((): void => {
    if (currentAccount) {
      dispatch(
        CartSlice.actions.removeItem({
          accountNumber: currentAccount.accountNumber,
          product: item as CartItem,
        })
      );
      removeCommerceItem(parseInt(item.productId.toString(), 10));

      toast("Item Removed", {
        type: "success",
        icon: <ToastIcon>check_circle</ToastIcon>,
      });
    }
  }, [currentAccount, dispatch, item, removeCommerceItem]);

  return { notifyCartUpdated, handleAddToCart, setQuantity, removeItem };
};
